/**
 * Chat message search policy — the match modes and length limits shared by the
 * search routes (api-service) and the chat search panels (webapp).
 *
 * Lives here, not in `@fias/db-types`, because the webapp depends on this
 * zero-dependency package but not on db-types. One copy means the client can't
 * drift from what the server validates (a drifted minimum would surface as a
 * 400 on every short query).
 */

/**
 * How search text is matched against message content:
 * - `word` — whole words, stemmed (`deploy` finds "deploys"); the server default.
 * - `contains` — the literal text anywhere, including mid-word (`oge` finds
 *   "booger"). Needs {@link CHAT_SEARCH_CONTAINS_MIN_LENGTH} characters.
 */
export const CHAT_SEARCH_MATCH_MODES = ['word', 'contains'] as const;

export type ChatSearchMatchMode = (typeof CHAT_SEARCH_MATCH_MODES)[number];

/** Server cap on search text length. */
export const CHAT_SEARCH_MAX_QUERY_LENGTH = 500;

/** Shortest search text worth sending in `word` mode. */
export const CHAT_SEARCH_MIN_QUERY_LENGTH = 2;

/** Minimum `contains` length — a trigram index cannot serve shorter substrings. */
export const CHAT_SEARCH_CONTAINS_MIN_LENGTH = 3;

/** Minimum search text length for a match mode. */
export function chatSearchMinQueryLength(mode: ChatSearchMatchMode): number {
  return mode === 'contains' ? CHAT_SEARCH_CONTAINS_MIN_LENGTH : CHAT_SEARCH_MIN_QUERY_LENGTH;
}

/**
 * Content filters a message search can apply (`has=` on the search routes,
 * `has:` in the search box):
 * - `file` — the message carries at least one attachment.
 * - `image` — at least one attachment is an image (by MIME type).
 * - `link` — the message content contains a hyperlink.
 *
 * A content filter counts as a search on its own ("everything with a file in
 * here"), like a sender filter does; dates never do.
 */
export const CHAT_SEARCH_HAS_FILTERS = ['file', 'image', 'link'] as const;

export type ChatSearchHasFilter = (typeof CHAT_SEARCH_HAS_FILTERS)[number];

/** Type guard for a `has` value read from untrusted input (a parsed operator). */
export function isChatSearchHasFilter(value: string): value is ChatSearchHasFilter {
  return (CHAT_SEARCH_HAS_FILTERS as readonly string[]).includes(value);
}
